'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface ServiceDetail {
  icon: string;
  title: string;
  description: string;
  features: string[];
  badge?: string;
}

interface ServiceCategory {
  id: string;
  label: string;
  intro: string;
  services: ServiceDetail[];
}

const serviceData: ServiceCategory[] = [
  {
    id: 'application',
    label: 'Application Security',
    intro: 'Test the code, apps, and components your business runs on.',
    services: [
      {
        icon: '🌐',
        title: 'Web Application Security Testing',
        description: 'Comprehensive web application penetration testing built on OWASP Top 10 + SANS 25 methodology. Our AI-driven testing with human-led validation ensures zero false positives.',
        features: ['OWASP Top 10 coverage', 'Business logic testing', 'Authentication & session testing', 'API endpoint analysis', 'Real-time findings dashboard', 'AI-generated remediation'],
        badge: 'Most Popular',
      },
      {
        icon: '📱',
        title: 'Mobile Application Security Testing',
        description: 'Static + dynamic analysis for iOS and Android apps. MASVS, NIST, OWASP Mobile-aligned testing from first build to production at enterprise scale.',
        features: ['iOS & Android coverage', 'Static code analysis', 'Dynamic runtime testing', 'Binary analysis', 'Network traffic analysis', 'MASVS compliance mapping'],
      },
      {
        icon: '🔌',
        title: 'API Security Testing',
        description: 'REST, GraphQL, and SOAP API security testing. Automated, validated, pipeline-native with zero slowdown. Integrates directly into your CI/CD workflow.',
        features: ['REST & GraphQL testing', 'Authentication bypass testing', 'Rate limiting analysis', 'Data exposure detection', 'CI/CD pipeline integration', 'Automated regression testing'],
      },
      {
        icon: '🔍',
        title: 'Secure Code Review',
        description: 'Human-led and automated review of application source code. OWASP and SANS Top 25 aligned. Identify vulnerabilities at the source before they reach production.',
        features: ['Multi-language support', 'SAST integration', 'Custom rule sets', 'Developer-friendly reports', 'IDE plugin support', 'Compliance mapping'],
      },
      {
        icon: '🤖',
        title: 'AI/LLM Pentesting',
        description: 'Adversarial emulations across LLMs, AI agents, GenAI apps, chatbots, and AI APIs. Test your AI systems against prompt injection, data poisoning, and model manipulation.',
        features: ['Prompt injection testing', 'Data poisoning detection', 'Model extraction attacks', 'Jailbreak testing', 'AI API security', 'GenAI compliance review'],
        badge: 'New',
      },
      {
        icon: '📦',
        title: 'Software Composition Analysis',
        description: 'Full SBOM/CBOM generation + CVE detection across every open-source component. Know exactly what\'s in your software and where the risks lie.',
        features: ['SBOM generation', 'CVE detection', 'License compliance', 'Dependency mapping', 'Continuous monitoring', 'Remediation prioritization'],
      },
    ],
  },
  {
    id: 'network',
    label: 'Network & Cloud Security',
    intro: 'Secure your infrastructure from the perimeter to the cloud.',
    services: [
      {
        icon: '☁️',
        title: 'Cloud Security Assessment',
        description: 'Comprehensive security assessment for AWS, Azure, and GCP environments. Identify misconfigurations, excessive permissions, and compliance gaps across your cloud infrastructure.',
        features: ['AWS, Azure, GCP coverage', 'IAM policy review', 'Misconfiguration detection', 'CIS benchmark compliance', 'Container security', 'Serverless security testing'],
        badge: 'Most Popular',
      },
      {
        icon: '🔒',
        title: 'Network Penetration Testing',
        description: 'Internal and external network penetration testing. Identify exploitable vulnerabilities across your entire network infrastructure before attackers do.',
        features: ['External perimeter testing', 'Internal network testing', 'Wireless security testing', 'VPN security review', 'Segmentation testing', 'Lateral movement simulation'],
      },
      {
        icon: '🛡️',
        title: 'Firewall & Configuration Review',
        description: 'In-depth review of firewall rules, network configurations, and security policies. Identify overly permissive rules and configuration weaknesses.',
        features: ['Firewall rule analysis', 'ACL review', 'Network topology review', 'Security policy audit', 'Compliance mapping', 'Remediation roadmap'],
      },
      {
        icon: '🔐',
        title: 'VPN & Zero Trust Assessment',
        description: 'Security assessment of VPN configurations, remote access solutions, and zero-trust implementations. Ensure your remote workforce is protected.',
        features: ['VPN configuration review', 'Zero trust gap analysis', 'MFA assessment', 'Access control review', 'Split tunneling analysis', 'Remote access security'],
      },
    ],
  },
  {
    id: 'offensive',
    label: 'Offensive & Advisory',
    intro: 'Advanced adversarial testing and strategic security guidance.',
    services: [
      {
        icon: '⚔️',
        title: 'Red Team Operations',
        description: 'Full-scope adversarial simulations mimicking real-world threat actors. Test your detection and response capabilities against sophisticated, multi-stage attacks.',
        features: ['Full kill chain simulation', 'Social engineering', 'Physical security testing', 'Custom threat scenarios', 'Purple team exercises', 'Executive reporting'],
        badge: 'Advanced',
      },
      {
        icon: '🎯',
        title: 'Threat Modeling',
        description: 'Systematic analysis of potential threats to your systems. Identify attack vectors, prioritize risks, and build security into your architecture from the ground up.',
        features: ['STRIDE methodology', 'Attack tree analysis', 'Risk prioritization', 'Architecture review', 'Developer workshops', 'Threat intelligence integration'],
      },
      {
        icon: '👤',
        title: 'vCISO Services',
        description: 'Virtual CISO advisory services. Strategic security leadership without the full-time executive cost. Get expert guidance on security strategy, governance, and risk management.',
        features: ['Security strategy development', 'Board-level reporting', 'Risk management', 'Security program management', 'Vendor risk assessment', 'Incident response planning'],
      },
      {
        icon: '📋',
        title: 'Security Architecture Review',
        description: 'Expert review of your security architecture, identifying gaps and providing actionable recommendations to strengthen your overall security posture.',
        features: ['Architecture gap analysis', 'Security control review', 'Technology stack assessment', 'Zero trust roadmap', 'Security maturity assessment', 'Remediation planning'],
      },
    ],
  },
  {
    id: 'iot',
    label: 'IoT & OT Security',
    intro: 'Specialized security for connected devices and industrial systems.',
    services: [
      {
        icon: '🏭',
        title: 'OT/ICS Security Assessment',
        description: 'Specialized security assessments for operational technology and industrial control systems. Protect your critical infrastructure without disrupting operations.',
        features: ['ICS/SCADA assessment', 'PLC security testing', 'Network segmentation review', 'Protocol analysis', 'Vendor risk assessment', 'IEC 62443 compliance'],
        badge: 'Specialized',
      },
      {
        icon: '📡',
        title: 'IoT Device Security Testing',
        description: 'Firmware analysis, hardware testing, and communication protocol security for IoT devices. Identify vulnerabilities before your devices reach the market.',
        features: ['Firmware extraction & analysis', 'Hardware interface testing', 'Protocol fuzzing', 'Bluetooth/WiFi testing', 'Cloud backend testing', 'OWASP IoT Top 10'],
      },
      {
        icon: '🔧',
        title: 'SCADA Security Review',
        description: 'Comprehensive security review of SCADA systems and industrial networks. Ensure your operational technology is protected against cyber threats.',
        features: ['SCADA architecture review', 'HMI security testing', 'Historian security', 'Remote access security', 'Patch management review', 'Incident response planning'],
      },
    ],
  },
];

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState('application');

  const activeCategory = serviceData.find((c) => c.id === activeTab) || serviceData[0];

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', overflowX: 'hidden' }}>
      <Navbar />

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #07182a 0%, #0a1d2f 50%, #0d2640 100%)',
        paddingTop: '140px',
        paddingBottom: '80px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(34,197,94,0.07) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div style={{ maxWidth: '90%', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '100px', padding: '6px 16px', marginBottom: '20px' }}>
            <span style={{ fontSize: '13px', color: '#22c55e', fontWeight: 500 }}>20+ Test Types Available</span>
          </div>
          <h1 style={{ fontSize: '48px', fontWeight: 700, color: '#fff', lineHeight: 1.2, margin: '0 0 20px' }}>
            Comprehensive <span style={{ color: '#22c55e' }}>Cybersecurity Services</span>
          </h1>
          <p style={{ fontSize: '18px', color: '#9ca3af', maxWidth: '700px', margin: '0 auto 32px', lineHeight: '1.7' }}>
            Human-led pentesting and continuous AI validation. Our CREST-aligned, OSCP- and CEH-certified testers deliver findings in real time — no waiting weeks for a PDF.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link href="/contact" style={{ padding: '12px 32px', background: '#22c55e', borderRadius: '100px', color: '#fff', textDecoration: 'none', fontSize: '16px', fontWeight: 600, transition: 'background 0.18s' }} className="hover:bg-green-600">
              Get a Free Quote
            </Link>
            <Link href="/contact" style={{ padding: '12px 32px', background: 'transparent', borderRadius: '100px', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', textDecoration: 'none', fontSize: '16px', fontWeight: 400, transition: 'border-color 0.18s' }} className="hover:border-green-400 hover:text-green-400">
              Book a Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Service tabs */}
      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto' }}>
          {/* Tab buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '32px' }}>
            {serviceData.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                style={{
                  padding: '12px 22px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: activeTab === cat.id ? '#fff' : '#1a1a1a',
                  background: activeTab === cat.id
                    ? 'linear-gradient(96deg, #07182a -70%, #000 4%, #1a5c2a 141%, #22c55e 148%)'
                    : '#fff',
                  border: activeTab === cat.id ? '1px solid transparent' : '1px solid #0a1d2f',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <p style={{ fontSize: '15px', color: '#374151', marginBottom: '32px', fontWeight: 500 }}>{activeCategory.intro}</p>

          {/* Service cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
            {activeCategory.services.map((service) => (
              <ServiceDetailCard key={service.title} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* Why TechLabs */}
      <section style={{ background: '#f9fafb', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 16px' }}>
              Why Choose <span style={{ color: '#22c55e' }}>TechLabs?</span>
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
              We combine the speed of AI with the depth of human expertise to deliver security testing that actually matters.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' }}>
            {[
              { icon: '🌟', title: 'CREST-Aligned Methodology', desc: 'Our testing follows CREST-aligned methodology with OSCP, CEH, and CISM certified professionals.' },
              { icon: '⚡', title: 'Real-Time Findings', desc: 'Findings appear as they\'re discovered — no waiting weeks for a final PDF report.' },
              { icon: '🤖', title: 'AI-Validated Results', desc: 'Every finding is validated by AI before reaching your team. Near-zero false positives.' },
              { icon: '🔒', title: 'Zero Disruption', desc: 'Safe, non-destructive testing that works around your schedule and production environment.' },
              { icon: '📊', title: 'Compliance-Ready Evidence', desc: 'Security work automatically maps to compliance frameworks. One test, multiple certifications.' },
              { icon: '📞', title: 'Dedicated Support', desc: 'Direct access to your testing team throughout the engagement, not just a ticketing system.' },
            ].map((item) => (
              <div key={item.title} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', textAlign: 'center', transition: 'transform 0.2s, box-shadow 0.2s' }} className="hover:-translate-y-1 hover:shadow-md">
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 8px' }}>{item.title}</h3>
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: '#0b0e14', padding: '80px 0' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center', padding: '0 5%' }}>
          <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>Ready to Find Your Vulnerabilities?</h2>
          <p style={{ fontSize: '16px', color: '#9ca3af', margin: '0 0 32px', lineHeight: '1.6' }}>
            Get a free scoping call with our security experts. We\'ll assess your environment and recommend the right testing approach.
          </p>
          <Link href="/contact" style={{ display: 'inline-flex', alignItems: 'center', padding: '14px 36px', background: '#22c55e', borderRadius: '100px', color: '#fff', textDecoration: 'none', fontSize: '17px', fontWeight: 600, transition: 'background 0.18s, box-shadow 0.18s' }} className="hover:bg-green-600 hover:shadow-lg">
            Get a Free PTaaS Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ServiceDetailCard({ service }: { service: ServiceDetail }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        position: 'relative',
        background: 'transparent',
        boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.06)' : 'rgb(230,230,230) 0 0 0 1px, rgba(0,0,0,0.05) 0 7px 22px 0',
        borderRadius: '10px',
        padding: '28px',
        overflow: 'hidden',
        transition: 'box-shadow 0.7s',
        cursor: 'default',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && (
        <div style={{
          position: 'absolute', width: '200%', height: '200%', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'conic-gradient(transparent 60%, #22c55e 78%, #4ade80 88%, transparent 100%)',
          animation: 'rotateBorder 3s linear infinite', zIndex: 0,
        }} />
      )}
      <div style={{ position: 'absolute', inset: '1.5px', borderRadius: '9px', background: '#fafafa', zIndex: 1 }} />
      <div style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
          <span style={{ fontSize: '36px' }}>{service.icon}</span>
          {service.badge && (
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#22c55e', background: '#dcfce7', padding: '3px 10px', borderRadius: '100px', border: '1px solid rgba(34,197,94,0.3)' }}>
              {service.badge}
            </span>
          )}
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#22c55e', margin: '0 0 10px' }}>{service.title}</h3>
        <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: '0 0 20px' }}>{service.description}</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {service.features.map((f) => (
            <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
              <span style={{ width: '16px', height: '16px', background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4l2 2 3-3" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              {f}
            </li>
          ))}
        </ul>
        <Link href="/contact" style={{ fontSize: '13px', color: '#22c55e', fontWeight: 700, textDecoration: 'none' }}>Get a Quote →</Link>
      </div>
      <style>{`
        @keyframes rotateBorder {
          0% { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
